<template>
  <ExpandableItem class="profile-children expander">
    <template #item>
      <div class="expander-label">
        {{ titleRef }} <span class="count">({{ props.family.childIds.length }})</span>
      </div>
    </template>
    <template #content>
      <div class="children-area">
        <ul v-if="props.family.childIds.length > 0" class="list-unstyled children-list">
          <li v-for="childId in props.family.childIds" :key="childId">
            <ProfileCard 
              v-if="childId == viewPersonRef?.id"
              :person-id="childId"
              this-profile/>
            <ProfileRelationshipCard v-else 
              :rel-person-id="childId"/>
          </li>
        </ul>
        <div class="action-row">
          <button type="button" v-if="canAddRef" class="btn btn-inline btn-link action" @click="emit('addChild', props.family)">
            {{ addChildTextRef }}
          </button>
          <button type="button" 
            v-if="canChangeOrderRef" 
            class="btn btn-inline btn-link action" 
            @click="changeChildOrder">
            Change order
          </button>
        </div>
      </div>
    </template>
  </ExpandableItem>
</template>

<style lang="scss" scoped>
.expander {
  margin-left: 2rem;
  font-size: 0.875rem;
}

.expander-label {
  color: #888;

  &:hover {
    color: #333;
  }
}

.count {
  font-size: 0.75rem;
  color: #aaa;
}

.empty-list {
  color: #aaa;
}

.children-area {
  padding-top: 0.5rem; // margin-top causes jumping when expanding (??)
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  .list-header {
    margin-bottom: 0.5rem;
    font-size: 0.75rem;
    color: #888;
  }

  .children-list {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .action-row {
    display: flex;
    gap: 1rem;

    .action {
      font-size: 0.875rem;
    }
  }
}
</style>

<script lang="ts" setup>
import { computed } from 'vue'
import { FamilyAndChildren } from '@/rd/ViewFamilyStore'
import { useViewPersonStore } from '@/rd/ViewPersonStore'
import { CompositeId } from '@/rd/CompositeId'
import { canCreateChildRelationship } from './ProfileManager'
import { notImplemented } from '@/util/AppUtil'
import ProfileCard from '@/manage/ProfileCard.vue'
import ProfileRelationshipCard from './ProfileRelationshipCard.vue'
import ExpandableItem from '@/util/ExpandableItem.vue'

const props = defineProps<{
  personId: string
  family: FamilyAndChildren
}>()

const emit = defineEmits([
  "addChild",
  "remove",
])

const viewPersonStore = useViewPersonStore()

const viewPersonRef = computed(() => viewPersonStore.getLoadedPerson(props.personId))
const isChildRef = computed(() => viewPersonRef.value && props.family.childIds.includes(viewPersonRef.value.id!))
const titleRef = computed(() => isChildRef.value ? "Siblings" : "Children")
const ownerGroupIdRef = computed(() => CompositeId.getGroupId(props.personId))
const hasOwnedFamilyRef = computed(() => props.family.family.matchIds.some(id => CompositeId.hasGroupId(id, ownerGroupIdRef.value!)))
const canAddRef = computed(() => hasOwnedFamilyRef.value && canCreateChildRelationship(undefined, props.family.family))
const canChangeOrderRef = computed(() => props.family.childIds.length > 1)
const addChildTextRef = computed(() => isChildRef.value ? "Add a sibling" : "Add a child")

function changeChildOrder() {
  notImplemented()
}
</script>
